// Public API
export { AccountDurableObject } from "./account-do";
export { SpaceDurableObject, SpaceIndexDurableObject } from "./spaces";
export type { PDSEnv, DataLocation } from "./types";

import { Hono } from "hono";
import { cors } from "hono/cors";
import { env as _env } from "cloudflare:workers";
import { Secp256k1Keypair } from "@atproto/crypto";
import { isDid, isHandle } from "@atcute/lexicons/syntax";
import { requireAuth } from "./middleware/auth";
import { DidResolver } from "./did-resolver";
import { WorkersDidCache } from "./did-cache";
import {
	handleXrpcProxy,
	handleGetFeedProxy,
	handleCreateReportProxy,
} from "./xrpc-proxy";
import { createOAuthApp } from "./oauth";
import * as sync from "./xrpc/sync";
import * as repo from "./xrpc/repo";
import * as server from "./xrpc/server";
import * as identity from "./xrpc/identity";
import * as passkey from "./passkey";
import {
	renderPasskeyRegistrationPage,
	renderPasskeyErrorPage,
	getPasskeyUiCsp,
	PASSKEY_ERROR_CSP,
} from "./passkey-ui";
import { renderDashboard } from "./dashboard";
import {
	createSpacesApp,
	createSpacesAdminApp,
	createSpacesUnavailableApp,
} from "./spaces";
import type { PDSEnv } from "./types";

import { version } from "../package.json" with { type: "json" };

// Cast env to PDSEnv for type safety
const env = _env as PDSEnv;

// Validate required environment variables at module load
const required = [
	"DID",
	"HANDLE",
	"PDS_HOSTNAME",
	"AUTH_TOKEN",
	"SIGNING_KEY",
	"SIGNING_KEY_PUBLIC",
	"JWT_SECRET",
	"PASSWORD_HASH",
] as const;

for (const key of required) {
	if (!env[key]) {
		throw new Error(`Missing required environment variable: ${key}`);
	}
}

// Validate DID and handle formats
if (!isDid(env.DID)) {
	throw new Error(`Invalid DID format: ${env.DID}`);
}
if (!isHandle(env.HANDLE)) {
	throw new Error(`Invalid handle format: ${env.HANDLE}`);
}

const didResolver = new DidResolver({
	didCache: new WorkersDidCache(),
	timeout: 3000, // 3 second timeout for DID resolution
	plcUrl: "https://plc.directory",
});

// Lazy-loaded keypair for service auth
let keypairPromise: Promise<Secp256k1Keypair> | null = null;
function getKeypair(): Promise<Secp256k1Keypair> {
	if (!keypairPromise) {
		keypairPromise = Secp256k1Keypair.import(env.SIGNING_KEY);
	}
	return keypairPromise;
}

const app = new Hono<{ Bindings: PDSEnv }>();

// CORS middleware for all routes
app.use(
	"*",
	cors({
		origin: "*",
		allowMethods: ["GET", "POST", "PUT", "DELETE", "OPTIONS"],
		allowHeaders: ["*"],
		exposeHeaders: ["Content-Type"],
		maxAge: 86400,
	}),
);

// Helper to get Account DO stub with optional data location
function getAccountDO(env: PDSEnv) {
	const location = env.DATA_LOCATION;

	// "eu" is a jurisdiction (hard guarantee), everything else is a hint (best-effort)
	if (location === "eu") {
		const namespace = env.ACCOUNT.jurisdiction("eu");
		return namespace.get(namespace.idFromName("account"));
	}

	// Location hints (or "auto"/undefined = no constraint)
	const id = env.ACCOUNT.idFromName("account");
	if (location && location !== "auto") {
		return env.ACCOUNT.get(id, { locationHint: location });
	}

	return env.ACCOUNT.get(id);
}

// DID document for did:web resolution
app.get("/.well-known/did.json", (c) => {
	return c.json(identity.buildDidDocument(c.env));
});

// Handle verification for AT Protocol
app.get("/.well-known/atproto-did", (c) => {
	return new Response(c.env.DID, {
		headers: { "Content-Type": "text/plain" },
	});
});

// Health check - AT Protocol standard path
app.get("/xrpc/_health", async (c) => {
	try {
		const accountDO = getAccountDO(c.env);
		await accountDO.rpcHealthCheck();
		return c.json({ status: "ok", version: `cirrus ${version}` });
	} catch {
		return c.json({ status: "unhealthy", version: `cirrus ${version}` }, 503);
	}
});

// Homepage
app.get("/", (c) => {
	const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>☁️</title>
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body {
	min-height: 100vh;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	font-family: ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, Consolas, 'DejaVu Sans Mono', monospace;
	background: #f0f0f0;
	color: #000;
	padding: 2rem;
}
.cloud { font-size: clamp(4rem, 15vw, 10rem); line-height: 1; }
.name { font-size: clamp(1.5rem, 5vw, 3rem); font-weight: 700; letter-spacing: 0.2em; margin: 1rem 0; }
.what { font-size: clamp(0.8rem, 2vw, 1rem); color: #666; max-width: 300px; text-align: center; }
.handle { font-size: clamp(0.9rem, 2.5vw, 1.2rem); margin-top: 2rem; padding: 0.5rem 1rem; border: 2px solid #000; }
:is(.handle, .name) a { color: inherit; text-decoration: none; }
:is(.handle, .name) a:hover { text-decoration: underline; }
.version { position: fixed; bottom: 1rem; right: 1rem; font-size: 0.7rem; color: #999; }
</style>
</head>
<body>
<div class="cloud">☁️</div>
<div class="name"><a href="https://github.com/ascorbic/cirrus">CIRRUS</a></div>
<div class="what">a personal data server for the atmosphere</div>
<div class="handle"><a href="https://bsky.app/profile/${c.env.HANDLE}" target="_blank">@${c.env.HANDLE}</a></div>
<div class="version">v${version}</div>
</body>
</html>`;
	return c.html(html);
});

// Status dashboard
app.get("/status", (c) => {
	return c.html(
		renderDashboard({
			hostname: c.env.PDS_HOSTNAME,
			handle: c.env.HANDLE,
			did: c.env.DID,
			version,
			spacesEnabled: c.env.SPACES_ENABLED === "true",
		}),
	);
});

// Sync endpoints (federation)
app.get("/xrpc/com.atproto.sync.getRepo", (c) =>
	sync.getRepo(c, getAccountDO(c.env)),
);
app.get("/xrpc/com.atproto.sync.getRepoStatus", (c) =>
	sync.getRepoStatus(c, getAccountDO(c.env)),
);
app.get("/xrpc/com.atproto.sync.getLatestCommit", (c) =>
	sync.getLatestCommit(c, getAccountDO(c.env)),
);
app.get("/xrpc/com.atproto.sync.getBlocks", (c) =>
	sync.getBlocks(c, getAccountDO(c.env)),
);
app.get("/xrpc/com.atproto.sync.getBlob", (c) =>
	sync.getBlob(c, getAccountDO(c.env)),
);
app.get("/xrpc/com.atproto.sync.listRepos", (c) =>
	sync.listRepos(c, getAccountDO(c.env)),
);
app.get("/xrpc/com.atproto.sync.listBlobs", (c) =>
	sync.listBlobs(c, getAccountDO(c.env)),
);
app.get("/xrpc/com.atproto.sync.getRecord", (c) =>
	sync.getRecord(c, getAccountDO(c.env)),
);

// WebSocket firehose
app.get("/xrpc/com.atproto.sync.subscribeRepos", async (c) => {
	const upgradeHeader = c.req.header("Upgrade");
	if (upgradeHeader !== "websocket") {
		return c.json(
			{ error: "InvalidRequest", message: "Expected WebSocket upgrade" },
			400,
		);
	}

	// Use fetch() instead of RPC to avoid WebSocket serialization error
	const accountDO = getAccountDO(c.env);
	return accountDO.fetch(c.req.raw);
});

// Repository operations - handle local repo directly, proxy foreign DIDs to AppView
app.use("/xrpc/com.atproto.repo.describeRepo", async (c, next) => {
	const requestedRepo = c.req.query("repo");
	if (!requestedRepo || requestedRepo === c.env.DID) {
		return repo.describeRepo(c, getAccountDO(c.env));
	}
	await next();
});

app.use("/xrpc/com.atproto.repo.getRecord", async (c, next) => {
	const requestedRepo = c.req.query("repo");
	if (!requestedRepo || requestedRepo === c.env.DID) {
		return repo.getRecord(c, getAccountDO(c.env));
	}
	await next();
});

app.use("/xrpc/com.atproto.repo.listRecords", async (c, next) => {
	const requestedRepo = c.req.query("repo");
	if (!requestedRepo || requestedRepo === c.env.DID) {
		return repo.listRecords(c, getAccountDO(c.env));
	}
	await next();
});

// Write operations require authentication
app.post("/xrpc/com.atproto.repo.createRecord", requireAuth, (c) =>
	repo.createRecord(c, getAccountDO(c.env)),
);
app.post("/xrpc/com.atproto.repo.deleteRecord", requireAuth, (c) =>
	repo.deleteRecord(c, getAccountDO(c.env)),
);
app.post("/xrpc/com.atproto.repo.uploadBlob", requireAuth, (c) =>
	repo.uploadBlob(c, getAccountDO(c.env)),
);
app.post("/xrpc/com.atproto.repo.applyWrites", requireAuth, (c) =>
	repo.applyWrites(c, getAccountDO(c.env)),
);
app.post("/xrpc/com.atproto.repo.putRecord", requireAuth, (c) =>
	repo.putRecord(c, getAccountDO(c.env)),
);
app.post("/xrpc/com.atproto.repo.importRepo", requireAuth, (c) =>
	repo.importRepo(c, getAccountDO(c.env)),
);
app.get("/xrpc/com.atproto.repo.listMissingBlobs", requireAuth, (c) =>
	repo.listMissingBlobs(c, getAccountDO(c.env)),
);

// Server identity
app.get("/xrpc/com.atproto.server.describeServer", server.describeServer);
app.get(
	"/xrpc/com.atproto.server.getAccountInviteCodes",
	requireAuth,
	server.getAccountInviteCodes,
);

// Handle resolution - return our DID for our handle, let others fall through to proxy
app.use("/xrpc/com.atproto.identity.resolveHandle", async (c, next) => {
	const handle = c.req.query("handle");
	if (handle === c.env.HANDLE) {
		return c.json({ did: c.env.DID });
	}
	await next();
});

// Recommended PLC credentials for inbound migration
app.get(
	"/xrpc/com.atproto.identity.getRecommendedDidCredentials",
	requireAuth,
	identity.getRecommendedDidCredentials,
);

// Identity management for outbound migration
// These endpoints allow migrating FROM Cirrus to another PDS
app.post(
	"/xrpc/com.atproto.identity.requestPlcOperationSignature",
	requireAuth,
	identity.requestPlcOperationSignature,
);
app.post(
	"/xrpc/com.atproto.identity.signPlcOperation",
	requireAuth,
	identity.signPlcOperation,
);
app.post(
	"/xrpc/com.atproto.identity.submitPlcOperation",
	requireAuth,
	identity.submitPlcOperation,
);
app.get(
	"/xrpc/gg.mk.experimental.getMigrationToken",
	requireAuth,
	identity.getMigrationToken,
);

// Session management
app.post("/xrpc/com.atproto.server.createSession", (c) =>
	server.createSession(c, getAccountDO(c.env)),
);
app.post("/xrpc/com.atproto.server.refreshSession", (c) =>
	server.refreshSession(c, getAccountDO(c.env)),
);
app.get("/xrpc/com.atproto.server.getSession", (c) =>
	server.getSession(c, getAccountDO(c.env)),
);
app.post("/xrpc/com.atproto.server.deleteSession", server.deleteSession);

// App passwords
app.post("/xrpc/com.atproto.server.createAppPassword", requireAuth, (c) =>
	server.createAppPassword(c, getAccountDO(c.env)),
);
app.get("/xrpc/com.atproto.server.listAppPasswords", requireAuth, (c) =>
	server.listAppPasswords(c, getAccountDO(c.env)),
);
app.post("/xrpc/com.atproto.server.revokeAppPassword", requireAuth, (c) =>
	server.revokeAppPassword(c, getAccountDO(c.env)),
);

// Account lifecycle
app.get("/xrpc/com.atproto.server.checkAccountStatus", requireAuth, (c) =>
	server.checkAccountStatus(c, getAccountDO(c.env)),
);
app.post("/xrpc/com.atproto.server.activateAccount", requireAuth, (c) =>
	server.activateAccount(c, getAccountDO(c.env)),
);
app.post("/xrpc/com.atproto.server.deactivateAccount", requireAuth, (c) =>
	server.deactivateAccount(c, getAccountDO(c.env)),
);
app.post("/xrpc/gg.mk.experimental.resetMigration", requireAuth, (c) =>
	server.resetMigration(c, getAccountDO(c.env)),
);
app.post(
	"/xrpc/com.atproto.server.requestEmailUpdate",
	requireAuth,
	server.requestEmailUpdate,
);
app.post(
	"/xrpc/com.atproto.server.requestEmailConfirmation",
	requireAuth,
	server.requestEmailConfirmation,
);
app.post("/xrpc/com.atproto.server.updateEmail", requireAuth, (c) =>
	server.updateEmail(c, getAccountDO(c.env)),
);

// Service auth - used by clients to get JWTs for external services (video, etc.)
app.get(
	"/xrpc/com.atproto.server.getServiceAuth",
	requireAuth,
	server.getServiceAuth,
);

// Actor preferences
app.get("/xrpc/app.bsky.actor.getPreferences", requireAuth, async (c) => {
	const accountDO = getAccountDO(c.env);
	const result = await accountDO.rpcGetPreferences();
	return c.json(result);
});
app.post("/xrpc/app.bsky.actor.putPreferences", requireAuth, async (c) => {
	const body = await c.req.json<{ preferences: unknown[] }>();
	const accountDO = getAccountDO(c.env);
	await accountDO.rpcPutPreferences(body.preferences);
	return c.json({});
});

// Age assurance (stub - self-hosted users are pre-verified)
app.get("/xrpc/app.bsky.ageassurance.getState", requireAuth, (c) => {
	return c.json({
		state: {
			status: "assured",
			access: "full",
			lastInitiatedAt: new Date().toISOString(),
		},
		metadata: {
			accountCreatedAt: new Date().toISOString(),
		},
	});
});

// Emit identity event to refresh handle verification with relays
app.post(
	"/xrpc/gg.mk.experimental.emitIdentityEvent",
	requireAuth,
	async (c) => {
		const accountDO = getAccountDO(c.env);
		const result = await accountDO.rpcEmitIdentityEvent(c.env.HANDLE);
		return c.json(result);
	},
);

// Firehose status (authenticated)
app.get(
	"/xrpc/gg.mk.experimental.getFirehoseStatus",
	requireAuth,
	async (c) => {
		const accountDO = getAccountDO(c.env);
		return c.json(await accountDO.rpcGetFirehoseStatus());
	},
);

// ============================================
// Passkey Routes
// ============================================

// Initialize passkey registration (authenticated)
app.post("/passkey/init", requireAuth, async (c) => {
	const accountDO = getAccountDO(c.env);
	const body = await c.req
		.json<{ name?: string }>()
		.catch(() => ({}) as { name?: string });
	try {
		const result = await passkey.initPasskeyRegistration(
			accountDO,
			c.env.PDS_HOSTNAME,
			c.env.DID,
			body.name,
		);
		return c.json(result);
	} catch (err) {
		console.error("Passkey init error:", err);
		const message = err instanceof Error ? err.message : String(err);
		return c.json({ error: "PasskeyInitFailed", message }, 500);
	}
});

// Passkey registration page (GET - renders UI)
app.get("/passkey/register", async (c) => {
	const token = c.req.query("token");
	if (!token) {
		return c.html(
			renderPasskeyErrorPage(
				"missing_token",
				"No registration token provided.",
			),
			400,
			{ "Content-Security-Policy": PASSKEY_ERROR_CSP },
		);
	}

	const accountDO = getAccountDO(c.env);
	const options = await passkey.getRegistrationOptions(
		accountDO,
		c.env.PDS_HOSTNAME,
		c.env.DID,
		token,
	);

	if (!options) {
		return c.html(
			renderPasskeyErrorPage(
				"invalid_token",
				"Invalid or expired registration token.",
			),
			400,
			{ "Content-Security-Policy": PASSKEY_ERROR_CSP },
		);
	}

	const csp = await getPasskeyUiCsp();
	return c.html(
		renderPasskeyRegistrationPage({
			options,
			token,
			handle: c.env.HANDLE,
		}),
		200,
		{ "Content-Security-Policy": csp },
	);
});

// Complete passkey registration (POST - receives WebAuthn response)
app.post("/passkey/register", async (c) => {
	const body = await c.req.json<{
		token: string;
		response: any;
	}>();

	if (!body.token || !body.response) {
		return c.json({ success: false, error: "Missing token or response" }, 400);
	}

	const accountDO = getAccountDO(c.env);
	// Name comes from the token (set during init)
	const result = await passkey.completePasskeyRegistration(
		accountDO,
		c.env.PDS_HOSTNAME,
		body.token,
		body.response,
	);

	if (result.success) {
		return c.json({ success: true });
	} else {
		return c.json({ success: false, error: result.error }, 400);
	}
});

// List passkeys (authenticated)
app.get("/passkey/list", requireAuth, async (c) => {
	const accountDO = getAccountDO(c.env);
	const passkeys = await passkey.listPasskeys(accountDO);
	return c.json({ passkeys });
});

// Delete passkey (authenticated)
app.post("/passkey/delete", requireAuth, async (c) => {
	const body = await c.req.json<{ id: string }>();
	if (!body.id) {
		return c.json({ success: false, error: "Missing passkey ID" }, 400);
	}

	const accountDO = getAccountDO(c.env);
	const deleted = await passkey.deletePasskey(accountDO, body.id);
	return c.json({ success: deleted });
});

// OAuth 2.1 endpoints for "Login with Bluesky"
const oauthApp = createOAuthApp(getAccountDO);
app.route("/", oauthApp);

// Atproto spaces (alpha). When the flag is off none of the space or
// simplespace routes are registered, so they fall through to the proxy
// like any unknown method, and the DID document says nothing about spaces.
//
// The bindings check must not throw: deploy-time startup validation
// evaluates this scope with vars visible but Durable Object bindings not
// yet materialized, and a real misconfiguration should degrade spaces to
// 503s rather than fail worker startup.
if (env.SPACES_ENABLED === "true") {
	if (env.SPACES && env.SPACES_INDEX) {
		app.route("/", createSpacesApp({ env, didResolver, getKeypair }));
	} else {
		console.error(
			"SPACES_ENABLED is set but the SPACES / SPACES_INDEX Durable Object bindings are missing. Space endpoints will return 503.",
		);
		app.route("/", createSpacesUnavailableApp());
	}
}
// The operator-only admin surface (spaces status and reset) mounts
// whenever the bindings exist, so `pds spaces reset` still works with the
// flag off or the space DOs in their schema-refusing state.
if (env.SPACES && env.SPACES_INDEX) {
	app.route("/", createSpacesAdminApp({ env, didResolver, getKeypair }));
}

// getFeed is proxied to the AppView but the service-auth JWT must be addressed
// to the feed generator, so it needs special handling ahead of the catch-all.
app.get("/xrpc/app.bsky.feed.getFeed", (c) =>
	handleGetFeedProxy(c, didResolver, getKeypair),
);

// createReport routes to a moderation labeler, not the AppView. Clients can
// override the labeler with the atproto-proxy header.
app.post("/xrpc/com.atproto.moderation.createReport", (c) =>
	handleCreateReportProxy(c, didResolver, getKeypair),
);

// Proxy unhandled XRPC requests to services specified via atproto-proxy header
// or fall back to Bluesky services for backward compatibility
app.all("/xrpc/*", (c) => handleXrpcProxy(c, didResolver, getKeypair));

export default app;
